/**
 * Bind a command's `deps` to the intent it runs under (P2 design §4.6: correlation is bound to
 * the operation, not ambient). The bound deps carry `intentId`, and:
 *
 * - every typed REST call (`services.api`) and forecast request (`services.forecastClient`)
 *   gets the intent in its options, which lib/apiClient.js and lib/forecastStream.js send as
 *   `x-intent-id`;
 * - every store action the command dispatches carries `meta.intent_id`, which the store trace
 *   (lib/telemetry/storeTrace.js) records.
 *
 * A handle with no id (the intent group off) returns `deps` unchanged, so production wiring and
 * tests behave exactly as before while intents are off. Injected fakes keep working: only the
 * methods present are wrapped, and an options argument is merged, never replaced.
 */
import type { CommandDeps, CommandServices, RunStore } from "../../commands/deps";

/** The position of each method's options argument, where `{intentId}` is merged in. */
const API_OPTIONS_ARG: Record<string, number> = {
  saveForecast: 1,
  createDerivedBranch: 2,
  generateDerivedBranchTriggers: 1,
  getForecastReportHeadline: 1,
  getForecastReportMass: 1,
  deleteForecast: 1,
  deleteSharedBranch: 1,
  archiveBranch: 1,
  cancel: 1,
  getResult: 1,
  submitPremiseCheck: 2,
  pollPremiseCheck: 3,
  preprocess: 2,
  health: 1,
  copySayerForecast: 2,
  adoptSayerForecast: 1,
};

/** The forecast client: the run request's `opts` / the branch's `parentContext`. */
const FORECAST_CLIENT_OPTIONS_ARG: Record<string, number> = {
  genForecastFromServer: 6,
  streamBranch: 4,
  // A resume stream prefers the run's own bound intent (lib/forecastStream.js) and falls back
  // to this one, the reattach's.
  resumeForecastStream: 2,
};

type AnyFn = (...args: any[]) => any;

function withOptionsAt(fn: AnyFn, pos: number, intentId: string): AnyFn {
  return (...args: any[]) => {
    const next = args.slice();
    const cur = next[pos];
    next[pos] = { ...(cur && typeof cur === "object" ? cur : {}), intentId };
    return fn(...next);
  };
}

function wrapMethods<T extends object>(
  target: T,
  positions: Record<string, number>,
  intentId: string,
): T {
  const out: Record<string, unknown> = { ...(target as Record<string, unknown>) };
  for (const [name, pos] of Object.entries(positions)) {
    const fn = out[name];
    if (typeof fn === "function") out[name] = withOptionsAt(fn as AnyFn, pos, intentId);
  }
  return out as T;
}

/** A store whose dispatched actions carry `meta.intent_id`. */
export function intentStore(store: RunStore, intentId: string): RunStore {
  return {
    getState: () => store.getState(),
    subscribe: (listener) => store.subscribe(listener),
    dispatch: (action) => {
      const a = action as unknown as { meta?: Record<string, unknown> };
      const tagged = { ...action, meta: { ...(a.meta ?? {}), intent_id: intentId } };
      return store.dispatch(tagged as unknown as typeof action);
    },
  };
}

/** `deps` bound to the intent `intent` (see the module comment). */
export function bindIntent<D extends CommandDeps>(
  deps: D,
  intent: { readonly id: string } | null | undefined,
): D & { intentId: string | null } {
  // A provisional intent (opened before any /api/me answer) is not correlated.
  const correlate = (intent as { correlate?: boolean } | null | undefined)?.correlate !== false;
  const intentId = (correlate && intent?.id) || "";
  const bound = (deps as { intentId?: unknown } | null)?.intentId;
  if (!intentId || !deps) {
    return bound === undefined ? { ...deps, intentId: null } : (deps as D & { intentId: null });
  }
  // Already bound to this intent (a command handing its deps to another): nothing to add.
  if (bound === intentId) return deps as D & { intentId: string };
  const services: CommandServices = deps.services
    ? {
        ...deps.services,
        api: deps.services.api
          ? wrapMethods(deps.services.api, API_OPTIONS_ARG, intentId)
          : deps.services.api,
        forecastClient: deps.services.forecastClient
          ? wrapMethods(deps.services.forecastClient, FORECAST_CLIENT_OPTIONS_ARG, intentId)
          : deps.services.forecastClient,
      }
    : deps.services;
  return {
    ...deps,
    intentId,
    store: deps.store ? intentStore(deps.store, intentId) : deps.store,
    services,
  };
}
